import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import config from './config';
import { Map, Marker, Popup } from 'react-map-gl';
import { FaPlus, FaMinus, FaSatellite, FaChevronLeft, FaChevronRight, FaArrowUp } from 'react-icons/fa';
import { MdMyLocation } from 'react-icons/md';
import axios from 'axios';

const DETAIL_NAVIGATION_DELAY_MS = 600;
const DEFAULT_FLY_TO_DURATION_MS = 800;
const RESTORE_FLY_TO_DURATION_MS = 0;
const HONG_KONG_CENTER = { longitude: 114.1694, latitude: 22.3193 };
const INITIAL_DEFAULT_ZOOM = 14;
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 60000,
};

// Haversine formula to calculate distance between two points on Earth
// Returns distance in kilometers
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km
  return distance;
};

// 取得地點封面圖（複製自 PortalHome）
function getCoverImage(it) {
  const fallback = '/default-cover.svg';
  if (!it) return fallback;
  try {
    // Check coverPhoto first
    if (it.coverPhoto) {
      const attrs = it.coverPhoto.attributes || it.coverPhoto;
      if (attrs.formats) {
        const f = attrs.formats;
        const url = f.small?.url || f.medium?.url || f.large?.url || f.thumbnail?.url;
        if (url) return normalizeUrl(url);
      }
      if (attrs.url) return normalizeUrl(attrs.url);
    }

    let first = null;
    if (Array.isArray(it.photos) && it.photos.length > 0) first = it.photos[0];
    else if (it.photos && Array.isArray(it.photos.data) && it.photos.data.length > 0) first = it.photos.data[0];
    else if (Array.isArray(it.images) && it.images.length > 0) first = it.images[0];
    else if (it.images && Array.isArray(it.images.data) && it.images.data.length > 0) first = it.images.data[0];
    if (first) {
      const attrs = first.attributes || first;
      if (attrs.formats) {
        const f = attrs.formats;
        const url = f.small?.url || f.medium?.url || f.large?.url || f.thumbnail?.url;
        if (url) return normalizeUrl(url);
      }
      if (attrs.url) return normalizeUrl(attrs.url);
      if (first.url) return normalizeUrl(first.url);
    }
  } catch (err) {}
  // fallback checks
  const tryPaths = [];
  tryPaths.push(it.image || it.coverImage || it.cover || it.photo || it.photos);
  tryPaths.push(it.images || it.pictures || it.media);
  const candidates = [];
  const pushIfStr = (v) => { if (typeof v === 'string' && v.trim()) candidates.push(v); };
  const extractFromArray = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const el of arr) {
      if (!el) continue;
      if (typeof el === 'string') candidates.push(el);
      else if (el.url) candidates.push(el.url);
      else if (el.attributes && (el.attributes.url || el.attributes.formats)) {
        candidates.push(el.attributes.url || (el.attributes.formats && (el.attributes.formats.large?.url || el.attributes.formats.small?.url)));
      }
    }
  };
  for (const p of tryPaths) {
    if (!p) continue;
    if (typeof p === 'string') pushIfStr(p);
    else if (Array.isArray(p)) extractFromArray(p);
    else if (p.data) {
      const arr = Array.isArray(p.data) ? p.data : [p.data];
      for (const d of arr) {
        if (!d) continue;
        if (d.attributes) {
          const a = d.attributes;
          if (a.url) candidates.push(a.url);
          if (a.formats) {
            const fmt = a.formats;
            candidates.push(fmt.large?.url || fmt.medium?.url || fmt.small?.url || fmt.thumbnail?.url);
          }
        }
        if (d.url) candidates.push(d.url);
      }
    } else if (p.attributes && p.attributes.url) {
      candidates.push(p.attributes.url);
    }
  }
  if (it.imageUrl) candidates.push(it.imageUrl);
  if (it.images && typeof it.images === 'string') candidates.push(it.images);
  const found = candidates.find(Boolean);
  if (!found) return fallback;
  return normalizeUrl(found);
}
function normalizeUrl(found) {
  try {
    if (!found) return '/default-cover.svg';
    if (found.startsWith('http')) return found;
    const base = config.API_URL && typeof config.API_URL === 'string' && config.API_URL.trim() !== ''
      ? config.API_URL.replace(/\/$/, '')
      : window.location.origin;
    return new URL(found, base).toString();
  } catch (err) {
    return found || '/default-cover.svg';
  }
}

// simple slugify for className generation
function slugify(s) {
  return (s || '').toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

const MapView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const params = new URLSearchParams(location.search);
  const latQ = parseFloat(params.get('lat'));
  const lngQ = parseFloat(params.get('lng'));
  const locIdQ = params.get('locId');
  const hasQuery = !isNaN(latQ) && !isNaN(lngQ);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(hasQuery ? { longitude: lngQ, latitude: latQ } : HONG_KONG_CENTER);
  const [currentMapStyle, setCurrentMapStyle] = useState('mapbox://styles/mapbox/streets-v11');
  // const [closestLocations, setClosestLocations] = useState([]); // 已不再使用
  const [userLocation, setUserLocation] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const sidebarWidth = 320;
  const [visibleTypes, setVisibleTypes] = useState({});
  const [typeColorMap, setTypeColorMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const mapRef = useRef(null);
  const [markerDelays, setMarkerDelays] = useState({});
  const sidebarRef = useRef(null);
  const detailNavigationTimeoutRef = useRef(null);
  const flyToDurationRef = useRef(DEFAULT_FLY_TO_DURATION_MS);
  const handledRestoreSearchRef = useRef(null);
  const didAutoCenterRef = useRef(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateViewport = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const selectLocation = (loc, options = {}) => {
    flyToDurationRef.current = options.flyToDuration ?? DEFAULT_FLY_TO_DURATION_MS;
    const dist = userLocation ? calculateDistance(userLocation.latitude, userLocation.longitude, loc.latitude, loc.longitude) : null;
    setSelectedLocation({ ...loc, distance: dist });
    
    // Close sidebar on mobile when location is selected
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const navigateToDetailWithPreview = (loc) => {
    const detailId = loc.documentId || loc.id;
    if (!detailId) return;

    const returnParams = new URLSearchParams();
    if (loc.latitude != null) returnParams.set('lat', String(loc.latitude));
    if (loc.longitude != null) returnParams.set('lng', String(loc.longitude));
    if (loc.id != null) returnParams.set('locId', String(loc.id));
    const returnPath = `${location.pathname}${returnParams.toString() ? `?${returnParams.toString()}` : ''}`;

    selectLocation(loc);

    if (detailNavigationTimeoutRef.current) {
      clearTimeout(detailNavigationTimeoutRef.current);
    }

    detailNavigationTimeoutRef.current = setTimeout(() => {
      navigate(`/location/${detailId}`, {
        state: {
          mapReturnPath: returnPath,
        },
      });
      detailNavigationTimeoutRef.current = null;
    }, DETAIL_NAVIGATION_DELAY_MS);
  };

  // Note: popup event passthrough handled via CSS in src/App.css

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${config.API_BASE}/api/locations?pagination[pageSize]=1000&populate=*`);
        const sourceArray = response.data.data ?? response.data ?? [];
        const rawItems = Array.isArray(sourceArray) ? sourceArray : [];
        const items = rawItems.map((it) => {
          const attrs = it.attributes ?? it;
          const latCandidates = [attrs.latitude, attrs.LATITUDE, attrs.lat, attrs.Latitude];
          const lonCandidates = [attrs.longitude, attrs.LONGITUDE, attrs.lon, attrs.Longitude];
          let latRaw = latCandidates.find((v) => v !== undefined && v !== null);
          let lonRaw = lonCandidates.find((v) => v !== undefined && v !== null);

          const geom = attrs.geometry ?? it.geometry ?? attrs.geom ?? attrs.SHAPE;
          if ((latRaw === undefined || lonRaw === undefined) && geom) {
            const coords = geom.coordinates ?? (geom.geometries ? geom.geometries[0]?.coordinates : undefined);
            if (Array.isArray(coords)) {
              lonRaw = lonRaw ?? coords[0];
              latRaw = latRaw ?? coords[1];
            }
          }

          const latitude = latRaw !== undefined && latRaw !== null ? parseFloat(latRaw) : NaN;
          const longitude = lonRaw !== undefined && lonRaw !== null ? parseFloat(lonRaw) : NaN;

          return {
            id: it.id ?? attrs.id,
            ...attrs,
            latitude,
            longitude,
          };
        }).filter((it) => Number.isFinite(it.latitude) && Number.isFinite(it.longitude));

        setLocations(items);

        // assign random staggered delays for drop animation
        const ids = items.map(i => i.id);
        // shuffle ids
        for (let i = ids.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [ids[i], ids[j]] = [ids[j], ids[i]];
        }
        const delays = {};
        // Ensure all animations finish within 1000ms total (delay + duration)
        const totalAllowed = 1000; // ms
        const animDuration = 350; // ms (matches CSS)
        const maxDelay = Math.max(0, totalAllowed - animDuration);
        ids.forEach((id, idx) => {
          const t = ids.length > 1 ? idx / (ids.length - 1) : 0;
          // distribute delays across range [0, maxDelay], add small jitter
          const jitter = Math.floor(Math.random() * 50) - 25; // +/-25ms
          const delay = Math.round(t * maxDelay + jitter);
          delays[id] = Math.max(0, delay);
        });
        setMarkerDelays(delays);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextUserLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        // Always store user location so distances can be computed,
        // but avoid overriding the map center when the URL already
        // contains lat/lng query parameters used for restoring state.
        setUserLocation(nextUserLocation);
        if (!hasQuery) {
          setMapCenter({ longitude: nextUserLocation.longitude, latitude: nextUserLocation.latitude });
        }
      },
      (error) => {
        console.warn('Error getting user location, fallback to Hong Kong center:', error);
        if (!hasQuery) {
          setMapCenter(HONG_KONG_CENTER);
        }
      },
      GEOLOCATION_OPTIONS
    );
  }, [hasQuery]);

  useEffect(() => {
    if (hasQuery || !userLocation || didAutoCenterRef.current || !mapRef.current) return;

    try {
      const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
      if (mapObj) {
        mapObj.flyTo({
          center: [userLocation.longitude, userLocation.latitude],
          zoom: INITIAL_DEFAULT_ZOOM,
          duration: 0,
        });
        didAutoCenterRef.current = true;
      }
    } catch (error) {
      console.warn('Auto-center to user location failed:', error);
    }
  }, [hasQuery, userLocation]);

  useEffect(() => {
    return () => {
      if (detailNavigationTimeoutRef.current) {
        clearTimeout(detailNavigationTimeoutRef.current);
      }
    };
  }, []);

  // 已不再使用 closestLocations

  // ensure visibleTypes contains known types and an aggregate Alert key
  useEffect(() => {
    const configTypes = Object.keys(config.FACILITY_TYPE_COLORS || {});
    const discovered = Array.from(new Set(locations.map(l => (l.facilityTypeEn || '').trim()).filter(Boolean)));
    const master = Array.from(new Set([...configTypes, ...discovered]));
    setVisibleTypes(prev => {
      const next = { ...prev };
      master.forEach(t => { if (!(t in next)) next[t] = true; });
      // compute available alert types (present in locations)
      const availableAlertTypes = master.filter(t => (t || '').trim().toLowerCase().endsWith('alert') && locations.some(l => (l.facilityTypeEn || '').trim() === t));
      if (!('Alert' in next)) {
        // default Alert aggregate to true if any child is visible
        next['Alert'] = availableAlertTypes.some(t => next[t] !== false);
      }
      return next;
    });
  }, [locations]);

  const configTypes = Object.keys(config.FACILITY_TYPE_COLORS || {});
  const discoveredTypes = Array.from(new Set(locations.map(l => (l.facilityTypeEn || '').trim()).filter(Boolean)));
  const masterTypes = Array.from(new Set([...configTypes, ...discoveredTypes]));
  const availableTypes = masterTypes.filter(t => locations.some(l => (l.facilityTypeEn || '').trim() === t));

  const isAlertType = (t) => (t || '').trim().toLowerCase().endsWith('alert');
  const availableAlertTypes = availableTypes.filter(isAlertType);
  const nonAlertAvailableTypes = availableTypes.filter(t => !isAlertType(t));

  // build a stable color map for facility types (use config first, fallback to palette or generated HSL)
  useEffect(() => {
    const cfg = (config.FACILITY_TYPE_COLORS && typeof config.FACILITY_TYPE_COLORS === 'object') ? config.FACILITY_TYPE_COLORS : {};
    const next = { ...cfg };
    const palette = ['#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9A6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'];
    const cTypes = Object.keys(cfg);
    const dTypes = Array.from(new Set(locations.map(l => (l.facilityTypeEn || '').trim()).filter(Boolean)));
    const mTypes = Array.from(new Set([...cTypes, ...dTypes]));
    mTypes.forEach((t, idx) => {
      if (!t) return;
      if (!(t in next)) {
        next[t] = palette[idx % palette.length] || `hsl(${(idx * 47) % 360} 70% 50%)`;
      }
    });
    setTypeColorMap(next);
  }, [locations]);

  // Fly / zoom to selected location
  useEffect(() => {
    if (!selectedLocation || !mapRef.current) return;
    try {
      const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
      if (mapObj && selectedLocation.longitude && selectedLocation.latitude) {
        mapObj.flyTo({ center: [selectedLocation.longitude, selectedLocation.latitude], zoom: 18, duration: flyToDurationRef.current });
        setMapCenter({ longitude: selectedLocation.longitude, latitude: selectedLocation.latitude });
      }
    } catch (err) {
      console.warn('flyTo failed', err);
    }
    // also scroll the sidebar list to the selected item
    try {
      const el = document.getElementById(`loc-${selectedLocation.id}`);
      if (el) {
        // scroll into view within the sidebar
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (err) {
      // ignore
    }
  }, [selectedLocation]);

  // Auto-select location by lat/lng or locId from query string (for popup)
  useEffect(() => {
    if (locations.length === 0) return;

    if (handledRestoreSearchRef.current === location.search) {
      return;
    }

    let targetLocation = null;

    if (locIdQ) {
      // Prioritize finding exact location by locId
      targetLocation = locations.find(loc => String(loc.id) === locIdQ);
    }

    if (!targetLocation && hasQuery) {
      // Fallback to finding closest location by lat/lng if no locId or locId not found
      let minDist = Infinity;
      let closest = null;
      locations.forEach(loc => {
        if (Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude)) {
          const dist = calculateDistance(latQ, lngQ, loc.latitude, loc.longitude);
          if (dist < minDist) {
            minDist = dist;
            closest = loc;
          }
        }
      });
      if (closest && minDist < 0.1) {
        targetLocation = closest;
      }
    }

    if (targetLocation) {
      selectLocation(targetLocation, { flyToDuration: RESTORE_FLY_TO_DURATION_MS });
      handledRestoreSearchRef.current = location.search;
    }
  }, [hasQuery, latQ, lngQ, locIdQ, location.search, locations, userLocation]);

  // popup scroll behavior handled via CSS (event passthrough)

  // Search function: check if searchTerm matches any attribute in the location
  const matchesSearch = (loc) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const str = JSON.stringify(loc).toLowerCase();
    return str.includes(term);
  };

  // prepare list items: show all visible locations (optionally with distance and sorted)
  const listItems = locations
    .filter(loc => (visibleTypes[(loc.facilityTypeEn || '').trim()] !== false))
    .filter(matchesSearch)
    .map(loc => ({
      ...loc,
      distance: userLocation ? calculateDistance(userLocation.latitude, userLocation.longitude, loc.latitude, loc.longitude) : null
    }))
    .sort((a, b) => {
      if (a.distance == null && b.distance == null) return 0;
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 50px)', width: '100%', position: 'relative', overflow: 'hidden' }}>

      {/* Map Section */}
      <div style={{ flex: 1, position: 'relative', width: '100%' }}>
        {/* Combined search and filter container */}
        <div className="search-tabs-container" style={{ position: 'absolute', top: 10, left: 10, zIndex: 20, background: 'transparent', padding: '6px 8px', borderRadius: 8, display: 'flex', flexDirection: isMobileViewport ? 'column' : 'row', alignItems: isMobileViewport ? 'stretch' : 'center', gap: 8, overflowX: 'auto', maxWidth: 'calc(100% - 20px)' }}>
          {/* Search input box */}
          <input
            type="text"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Trigger search, but since it's real-time, maybe not needed, but user requested on Enter
                // For now, keep real-time
              }
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              width: isMobileViewport ? '100%' : '200px',
              outline: 'none',
              flexShrink: 0
            }}
          />

          {/* Filter buttons */}
          <div style={{ display: 'flex', flexWrap: isMobileViewport ? 'wrap' : 'nowrap', gap: isMobileViewport ? 6 : 8 }}>
            {availableAlertTypes.length > 0 && (
              (() => {
                const alertVisible = visibleTypes['Alert'] !== false;
                const bg = alertVisible ? (typeColorMap['Alert'] || '#ff4757') : '#f1f1f1';
                const color = alertVisible ? '#fff' : '#333';
                return (
                  <button
                    key="__alert__"
                    className={`facility-tab facility-tab--alert ${alertVisible ? 'facility-tab--active' : ''}`}
                    onClick={() => setVisibleTypes(prev => {
                      const next = { ...prev };
                      const newVal = !(prev['Alert'] !== false);
                      next['Alert'] = newVal;
                      availableAlertTypes.forEach(t => { next[t] = newVal; });
                      return next;
                    })}
                    style={{
                      background: bg,
                      color,
                      border: 'none',
                      padding: isMobileViewport ? '5px 8px' : '6px 10px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: isMobileViewport ? '12px' : '14px',
                      flex: '0 1 auto',
                      boxShadow: alertVisible ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'
                    }}
                  >
                    Alert
                  </button>
                );
              })()
            )}
            {nonAlertAvailableTypes.map(type => {
              const isVisible = visibleTypes[type] !== false;
              const bg = isVisible ? (typeColorMap[type] || '#6c5ce7') : '#f1f1f1';
              const color = isVisible ? '#fff' : '#333';
              return (
                <button
                  key={type}
                  className={`facility-tab facility-tab--${slugify(type)} ${isVisible ? 'facility-tab--active' : ''}`}
                  onClick={() => setVisibleTypes(prev => {
                    const next = { ...prev };
                    const cur = prev[type] !== false;
                    next[type] = !cur;
                    // update aggregate Alert key
                    const anyChildVisible = availableAlertTypes.some(t => {
                      if (t === type) return !cur;
                      return prev[t] !== false;
                    });
                    next['Alert'] = anyChildVisible;
                    return next;
                  })}
                  style={{
                    background: bg,
                    color,
                    border: 'none',
                    padding: isMobileViewport ? '5px 8px' : '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontSize: isMobileViewport ? '12px' : '14px',
                    flex: '0 1 auto',
                    boxShadow: isVisible ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'
                  }}
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>

        <Map
          ref={mapRef}
          initialViewState={{ longitude: mapCenter.longitude, latitude: mapCenter.latitude, zoom: hasQuery ? 12 : INITIAL_DEFAULT_ZOOM }}
          mapboxAccessToken={config.MAPBOX_ACCESS_TOKEN}
          style={{ width: '100%', height: '100%' }}
          onMoveEnd={(evt) => setMapCenter({ longitude: evt.viewState.longitude, latitude: evt.viewState.latitude })}
          mapStyle={currentMapStyle}
        >
          {userLocation && (
            <Marker longitude={userLocation.longitude} latitude={userLocation.latitude}>
              <div style={{ cursor: 'pointer' }}>
                <img src="https://maps.google.com/mapfiles/ms/icons/blue-dot.png" alt="You are here" />
              </div>
            </Marker>
          )}

          {locations.filter(location => (visibleTypes[(location.facilityTypeEn || '').trim()] !== false) && matchesSearch(location)).map(location => {
            const facilityType = (location.facilityTypeEn || '').trim();
            const color = typeColorMap[facilityType] || (facilityType ? '#6c5ce7' : '#ff4757');

            return (
              <Marker
                key={location.id}
                longitude={location.longitude}
                latitude={location.latitude}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  selectLocation(location);
                }}
              >
                <div
                  className="drop-marker"
                  // apply per-marker animation delay
                  // eslint-disable-next-line react/style-prop-object
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animationDelay: markerDelays[location.id] ? `${markerDelays[location.id]}ms` : '0ms'
                  }}
                >
                  <span title={facilityType || '設施'} style={{ width: 18, height: 18, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 0 4px rgba(0,0,0,0.3)', display: 'inline-block' }} />
                </div>
              </Marker>
            );
          })}

          {selectedLocation && (
            <Popup className="popup-offset-40" longitude={selectedLocation.longitude} latitude={selectedLocation.latitude} onClose={() => setSelectedLocation(null)} anchor="top" offset={40}>
              <div className="popup-interactive" style={{ maxWidth: '250px' }}>
                {getCoverImage(selectedLocation) !== '/default-cover.svg' && (
                  <img
                    src={getCoverImage(selectedLocation)}
                    alt="Location cover"
                    style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }}
                  />
                )}
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{selectedLocation.nameTc || selectedLocation.nameEn || selectedLocation.name}</h3>
                {(selectedLocation.addressTc || selectedLocation.addressEn) && (
                  <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>地址：</strong>{selectedLocation.addressTc || selectedLocation.addressEn}</p>
                )}
                {(selectedLocation.facilityDetailsTc || selectedLocation.facilityDetailsEn) && (
                  <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>設施：</strong>{selectedLocation.facilityDetailsTc || selectedLocation.facilityDetailsEn}</p>
                )}
                {(selectedLocation.openingHoursTc || selectedLocation.openingHoursEn) && (
                  <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>開放時間：</strong>{selectedLocation.openingHoursTc || selectedLocation.openingHoursEn}</p>
                )}
                {selectedLocation.distance != null && (
                  <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: 'bold' }}>{selectedLocation.distance.toFixed(1)}km</p>
                )}
                {isAuthenticated && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToDetailWithPreview(selectedLocation);
                    }}
                    style={{
                      marginTop: '12px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                  >
                    詳情
                  </button>
                )}
              </div>
            </Popup>
          )}
          </Map>

          {/* bottom-right controls: North, Locate, Zoom In/Out, Toggle Satellite/Map */}
          <div style={{ position: 'absolute', right: isSidebarOpen ? `${sidebarWidth + 12}px` : '12px', bottom: 12, zIndex: 30, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              title="向北"
              aria-label="向北"
              onClick={() => {
                try {
                  const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
                  if (mapObj) mapObj.easeTo({ bearing: 0, pitch: 0, duration: 400 });
                } catch (err) { console.warn('north failed', err); }
              }}
              className="map-control-btn"
            ><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}><FaArrowUp size={12} /><span style={{ fontSize: '10px', fontWeight: 'bold' }}>N</span></div></button>

            <button
              title="現在位置"
              aria-label="現在位置"
              onClick={() => {
                try {
                  const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
                  if (userLocation && mapObj) {
                    mapObj.flyTo({ center: [userLocation.longitude, userLocation.latitude], zoom: 14, duration: 600 });
                    setMapCenter({ longitude: userLocation.longitude, latitude: userLocation.latitude });
                  } else if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((pos) => {
                      const lat = pos.coords.latitude; const lon = pos.coords.longitude;
                      setUserLocation({ latitude: lat, longitude: lon });
                      if (mapObj) mapObj.flyTo({ center: [lon, lat], zoom: 14, duration: 600 });
                    }, (err) => console.warn('geolocate failed', err));
                  }
                } catch (err) { console.warn('locate failed', err); }
              }}
              className="map-control-btn"
            ><MdMyLocation size={16} /></button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button
                title="放大"
                aria-label="放大"
                onClick={() => {
                  try {
                    const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
                    if (mapObj) {
                      const z = mapObj.getZoom();
                      mapObj.easeTo({ zoom: Math.min(20, z + 1), duration: 300 });
                    }
                  } catch (err) { console.warn('zoom in failed', err); }
                }}
                className="map-control-btn"
              ><FaPlus size={14} /></button>
              <button
                title="縮小"
                aria-label="縮小"
                onClick={() => {
                  try {
                    const mapObj = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
                    if (mapObj) {
                      const z = mapObj.getZoom();
                      mapObj.easeTo({ zoom: Math.max(0, z - 1), duration: 300 });
                    }
                  } catch (err) { console.warn('zoom out failed', err); }
                }}
                className="map-control-btn"
              ><FaMinus size={14} /></button>
            </div>

            <button
              title="切換衛星/地圖"
              aria-label="切換衛星/地圖"
              onClick={() => setCurrentMapStyle(prev => prev && prev.includes('satellite') ? 'mapbox://styles/mapbox/streets-v11' : 'mapbox://styles/mapbox/satellite-streets-v11')}
              className="map-control-btn map-control-toggle"
            ><FaSatellite size={16} /></button>
          </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={{
          position: 'absolute',
          top: '50%',
          right: isSidebarOpen ? '320px' : '0',
          transform: 'translateY(-50%)',
          zIndex: 10,
          background: 'white',
          border: '1px solid #ccc',
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          width: '30px',
          height: '60px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
          transition: 'right 0.3s ease-in-out',
        }}
      >
        <span className="map-toggle-btn" aria-hidden="true">{isSidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}</span>
      </button>

      {/* Closest Locations List Section (Sidebar) */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: '320px', height: '100%', background: 'white', padding: '15px', overflowY: 'auto', borderLeft: '1px solid #ccc', boxShadow: '-2px 0 5px rgba(0,0,0,0.1)', transform: isSidebarOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s ease-in-out', zIndex: 5, boxSizing: 'border-box' }}>
        <h2 style={{ marginTop: 0 }}>地點清單</h2>
        {listItems.length === 0 ? (
          <p>沒有找到地點。</p>
        ) : (
          <ul style={{ paddingLeft: '20px' }} ref={sidebarRef}>
            {listItems.map(loc => {
              const typeKey = (loc.facilityTypeEn || '').trim();
              const itemColor = typeColorMap[typeKey] || (typeKey ? '#6c5ce7' : '#cccccc');
              return (
                    <li id={`loc-${loc.id}`} key={loc.id} style={{ listStyle: 'none', marginBottom: '15px', cursor: 'pointer', padding: '10px', borderRadius: '5px', background: selectedLocation?.id === loc.id ? '#f0f8ff' : 'transparent', border: selectedLocation?.id === loc.id ? '1px solid #cce5ff' : '1px solid transparent', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }} onClick={() => { selectLocation(loc); }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <img src={getCoverImage(loc)} alt="cover" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, background: '#f3f3f3', border: '1px solid #eee', flex: '0 0 auto' }} />
                      <small style={{ color: '#0066cc', fontWeight: 'bold', marginLeft: '12px', fontSize: '15px' }}>{loc.distance ? loc.distance.toFixed(1) + 'km' : ''}</small>
                      <small style={{ color: '#666', marginLeft: '16px', fontSize: '14px', whiteSpace: 'nowrap' }}>區域：{loc.districtTc || loc.districtEn}</small>
                    </div>
                    <div style={{ flex: '1 1 auto', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 12, height: 12, borderRadius: '50%', background: itemColor, border: '1px solid rgba(0,0,0,0.08)', flex: '0 0 auto' }} />
                        <strong style={{ margin: 0, fontSize: '15px' }}>{loc.nameTc || loc.nameEn || loc.name}</strong>
                      </div>
                      {(loc.facilityDetailsTc || loc.facilityDetailsEn) && (
                        <small style={{ display: 'block', color: '#555', marginTop: '6px', lineHeight: '1.4' }}>
                          設施：{loc.facilityDetailsTc || loc.facilityDetailsEn}
                        </small>
                      )}
                    </div>
                  </li>
              );
            })}
          </ul>
        )}
      </div>

    </div>
  );
};

export default MapView;
